import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, Input, Select, TimePicker, Row, Col, Button, Divider, Spin
} from 'antd';
import { CloseOutlined, TeamOutlined, CalendarOutlined, SettingOutlined, UserOutlined, ScheduleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import { useDispatch } from 'react-redux';
import { batchService } from '../../services/batchService';
import StudentDatePicker from '../studentDatePicker';
import { getTenantId } from '../../services/helper';

const { Option } = Select;

export type BatchStatus = 'ACTIVE' | 'INACTIVE' | 'COMPLETED';

export interface BatchInput {
  _id?: string;
  batchName: string;
  instructorId?: string;
  staffId?: string;
  trainingType?: 'SIMULATION' | 'ON_ROAD' | null;
  startTime: string;
  endTime: string;
  studentIds?: string[];
  status: BatchStatus;
  maxStudents?: number;
}

interface BatchFormModalProps {
  open: boolean;
  title: string;
  onCancel: () => void;
  onFinish: (values: any) => void;
  form: any;
  initialValues?: any;
  isEdit?: boolean;
  isLoading?: boolean;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
}

const BatchFormModal: React.FC<BatchFormModalProps> = ({
  open, title, onCancel, onFinish, form, initialValues, isEdit, isLoading
}) => {
  const dispatch = useDispatch();
  const [instructors, setInstructors] = useState<User[]>([]);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [pendingFetch, setPendingFetch] = useState<{ startTime: string; endTime: string } | null>(null);
  const [timeSelected, setTimeSelected] = useState(false);
  const [selectedTrainingType, setSelectedTrainingType] = useState<string | null>(null);
  const [dateAssignedStudents, setDateAssignedStudents] = useState<any[]>([]);

  const watchedMaxStudents = Form.useWatch('maxStudents', form);
  const watchedStartTime = Form.useWatch('startTime', form);
  const watchedEndTime = Form.useWatch('endTime', form);

  const batchId = initialValues?._id;

  const getStudentId = (student: any) => {
    if (!student) return '';
    if (student._id) return String(student._id);
    if (student.studentId) {
      if (typeof student.studentId === 'object' && student.studentId._id) {
        return String(student.studentId._id);
      }
      return String(student.studentId);
    }
    return String(student);
  };

  useEffect(() => {
    if (open) {
      setInstructors([]);
      setStaffMembers([]);
      setTimeSelected(false);
      setSelectedTrainingType(initialValues?.trainingType || null);
      if (!isEdit) {
        setDateAssignedStudents([]);
      } else {
        setDateAssignedStudents(initialValues?.studentIds || []);
      }
      const timer = setTimeout(() => {
        try {
          const formValues = form.getFieldsValue();
          if (formValues?.startTime && formValues?.endTime) {
            setPendingFetch({ startTime: formValues.startTime, endTime: formValues.endTime });
          }
        } catch (e) { }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, initialValues, isEdit]);

  useEffect(() => {
    if (pendingFetch && open) {
      fetchAvailableUsers(pendingFetch.startTime, pendingFetch.endTime);
      setPendingFetch(null);
    }
  }, [pendingFetch, open, form]);

  const fetchAvailableUsers = async (startTime: string, endTime: string) => {
    setTimeSelected(true);
    setLoadingInstructors(true);
    try {
      const instructorRes = await batchService.getAvailableUsers({ role: 'INSTRUCTOR', startTime, endTime, tenantId: getTenantId() });
      setInstructors(instructorRes?.resultObject?.users || instructorRes?.users || []);
    } catch {
      setInstructors([]);
    } finally {
      setLoadingInstructors(false);
    }

    setLoadingStaff(true);
    try {
      const staffRes = await batchService.getAvailableUsers({ role: 'STAFF', startTime, endTime });
      setStaffMembers(staffRes?.resultObject?.users || staffRes?.users || []);
    } catch {
      setStaffMembers([]);
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleTimeRangeChange = (times: [Dayjs | null, Dayjs | null] | null) => {
    if (times && times[0] && times[1]) {
      const startTime = times[0].format('HH:mm');
      const endTime = times[1].format('HH:mm');
      form.setFieldsValue({ startTime, endTime, instructorId: undefined, studentIds: [] });
      fetchAvailableUsers(startTime, endTime);
    } else {
      setInstructors([]);
      setTimeSelected(false);
    }
  };

  const handleFinish = (vals: any) => {
    const studentAssignments = dateAssignedStudents
      .map((student: any) => {
        const studentId = getStudentId(student);
        const assignedDate = student.assignedDate || student.assignedDateAt || student.date;
        return studentId && assignedDate
          ? {
            studentId,
            assignedDate,
            enrollmentDaysRemaining: student.enrollmentDaysRemaining || 0,
            balanceClassDates: student.balanceClassDates || [],
            assignedAt: student.assignedAt || new Date().toISOString(),
          }
          : null;
      })
      .filter(Boolean);
    vals.studentIds = studentAssignments;
    vals.maxStudents = Number(vals.maxStudents ?? watchedMaxStudents ?? 3);
    onFinish(vals);
  };

  const handleAddStudentWithDate = (student: any, assignedDate: string) => {
    const studentId = getStudentId(student);
    if (!studentId) return;
    const newAssignment = {
      ...student,
      studentId,
      assignedDate,
      assignedAt: new Date().toISOString(),
    };
    setDateAssignedStudents(prev => {
      const alreadyAssigned = prev.some((s: any) => {
        const existingId = getStudentId(s);
        const existingDate = s.assignedDate || s.assignedDateAt || s.date;
        return existingId === studentId && existingDate === assignedDate;
      });
      return alreadyAssigned ? prev : [...prev, newAssignment];
    });
  };

  const handleRemoveStudentFromDate = (studentId: string, assignedDate?: string) => {
    setDateAssignedStudents(prev => prev.filter((s: any) => {
      const existingId = getStudentId(s);
      const existingDate = s.assignedDate || s.assignedDateAt || s.date;
      if (assignedDate) {
        return !(existingId === studentId && existingDate === assignedDate);
      }
      return existingId !== studentId;
    }));
  };

  return (
    <Drawer
      className="svc-drawer batch-drawer"
      open={open}
      onClose={onCancel}
      width={980}
      closable={false}
      destroyOnClose
      title={null}
      footer={
        <div className="svc-drawer-footer">
          <Button className="svc-btn-cancel" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="svc-btn-save"
            type="primary"
            onClick={() => form.submit()}
            loading={isLoading}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Batch'}
          </Button>
        </div>
      }
    >

      {isLoading && (
        <div className="svc-spinner-overlay">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 40, color: 'var(--primary-color)' }} spin />}
          />
          <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14 }}>
            Saving batch...
          </span>
        </div>
      )}

      <div className="svc-drawer-header">
        <div className="svc-drawer-header-inner">
          <div className="svc-drawer-title-group">
            <div className="svc-drawer-icon-box">
              <ScheduleOutlined />
            </div>
            <div className="svc-drawer-title-text">
              <span className="svc-drawer-title">{title}</span>
              <span className="svc-drawer-subtitle">Fill in the details to create or update a batch</span>
            </div>
          </div>
          <button className="svc-drawer-close-btn" onClick={onCancel} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
        <Divider className="svc-drawer-header-divider" />
      </div>

      {/* ── Two Column Layout ── */}
      <div className="batch-two-col">

        {/* ══ LEFT COLUMN: Form Fields ══ */}
        <div className="batch-col-left">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            className="svc-form"
            initialValues={{ status: 'ACTIVE', ...initialValues }}
          >

            {/* ── Section: Basic Information ── */}
            <div className="svc-form-section">

              <Form.Item
                name="batchName"
                label="Batch Name"
                rules={[{ required: true, message: 'Batch name is required' }]}
                className="svc-form-item"
              >
                <Input className="svc-input" placeholder="e.g. Morning Car Batch A" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="trainingType" label="Training Type" className="svc-form-item">
                    <Select
                      className="svc-select"
                      placeholder="Select training type"
                      allowClear
                      onChange={(value: string | undefined) => {
                        setSelectedTrainingType(value || null);
                        form.setFieldsValue({ instructorId: undefined, staffId: undefined });
                      }}
                    >
                      <Option value="SIMULATION">Simulation</Option>
                      <Option value="ON_ROAD">On-Road</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Please select status' }]}
                    className="svc-form-item"
                  >
                    <Select className="svc-select" placeholder="Select status">
                      <Option value="ACTIVE">Active</Option>
                      <Option value="INACTIVE">Inactive</Option>
                      <Option value="COMPLETED">Completed</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Divider className="svc-section-divider" />
            </div>



            {/* ── Section: Session Time ── */}
            <div className="svc-form-section">

              <Form.Item
                name="timeRange"
                label="Session Time (From — To)"
                rules={[{ required: true, message: 'Session time is required' }]}
                extra={isEdit ? 'Session time is locked and cannot be changed once the batch is created.' : undefined}
                className="svc-form-item"
              >
                <TimePicker.RangePicker
                  format="HH:mm"
                  style={{ width: '100%', height: 35, borderRadius: 10, border: '1px solid #e2e8f0', background: '#fafbfc', fontSize: 14 }}
                  minuteStep={15}
                  onChange={handleTimeRangeChange}
                  disabled={isEdit}
                />
              </Form.Item>

              {/* Hidden fields */}
              <Form.Item name="startTime" hidden><Input /></Form.Item>
              <Form.Item name="endTime" hidden><Input /></Form.Item>

              <Form.Item
                name="maxStudents"
                label="Max Students Per Day"
                rules={[{ required: true, message: 'Max students is required' }]}
                initialValue={3}
                className="svc-form-item"
              >
                <Select className="svc-select" placeholder="Select max students">
                  {[1, 2, 3, 4, 5, 7, 8].map(n => (
                    <Option key={n} value={n}>{n}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Divider className="svc-section-divider" />
            </div>


            {/* ── Section: Instructor ── */}
            <div className="svc-form-section svc-form-section--last">


              <Form.Item
                name="instructorId"
                label={selectedTrainingType === 'SIMULATION' ? 'Instructor / Staff' : 'Instructor'}
                className="svc-form-item"
                extra={
                  selectedTrainingType === 'ON_ROAD'
                    ? 'Only instructors assigned to a vehicle are listed. Create and assign a vehicle to the instructor first.'
                    : undefined
                }
              >
                <Select
                  className="svc-select"
                  placeholder={
                    !timeSelected
                      ? 'Select session time first'
                      : selectedTrainingType === 'SIMULATION'
                        ? 'Select instructor or staff'
                        : 'Select instructor'
                  }
                  loading={loadingInstructors || (selectedTrainingType === 'SIMULATION' && loadingStaff)}
                  disabled={!timeSelected || loadingInstructors || (selectedTrainingType === 'SIMULATION' && loadingStaff)}
                  allowClear
                  notFoundContent={timeSelected && !loadingInstructors ? 'No available users' : undefined}
                >
                  {instructors.map((instructor) => (
                    <Option key={instructor._id} value={instructor._id}>
                      {instructor.firstName} {instructor.lastName}
                    </Option>
                  ))}
                  {selectedTrainingType === 'SIMULATION' && staffMembers.map((staff) => (
                    <Option key={staff._id} value={staff._id}>
                      {staff.firstName} {staff.lastName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

          </Form>
        </div>

        {/* ══ RIGHT COLUMN: Student Calendar Assignment ══ */}
        <div className="batch-col-right">
          <div className="batch-col-right-inner">
            <div className="svc-section-header batch-cal-header">
              <TeamOutlined className="svc-section-icon" />
              <span className="svc-section-title">
                Student Assignment
                {dateAssignedStudents.length > 0 && (
                  <span className="svc-doc-count-badge">{dateAssignedStudents.length}</span>
                )}
              </span>
            </div>

            {!timeSelected ? (
              <div className="svc-doc-empty batch-cal-empty">
                <CalendarOutlined className="svc-doc-empty-icon" />
                <span className="svc-doc-empty-text">
                  Select a session time on the left to enable calendar assignment.
                </span>
              </div>
            ) : (
              <StudentDatePicker
                batchId={batchId || 'new-batch'}
                startTime={watchedStartTime || form.getFieldValue('startTime') || ''}
                endTime={watchedEndTime || form.getFieldValue('endTime') || ''}
                isOnRoad={selectedTrainingType === 'ON_ROAD' ? true : false}
                assignedStudents={dateAssignedStudents}
                onStudentDateAdd={handleAddStudentWithDate}
                onStudentRemove={handleRemoveStudentFromDate}
                maxStudentsPerDay={watchedMaxStudents ?? 3}
              />
            )}
          </div>
        </div>

      </div>
    </Drawer>
  );
};

export default BatchFormModal;